<template>
  <div class="navbar navbar-expand-lg bg-light px-3 py-2 shadow-sm z-3 fixed-top">
    <div
      class="container-fluid d-flex align-items-center justify-content-between"
      data-test-id="header-bar"
    >
      <div class="d-flex align-items-center flex-shrink-1 gap-4">
        <div class="d-flex align-items-center">
          <img
            src="@/assets/onera_logo.png"
            alt="Logo"
            class="me-2"
            style="height: 30px"
            data-test-id="onera_header_logo"
          />
          <span
            class="fw-light text-muted text-sm-start"
            data-test-id="onera_header_label"
            id="onera_header_label"
            >{{ $t('analysisService') }}</span
          >
        </div>
        <div class="vr"></div>
        <div class="d-flex align-items-center flex-wrap gap-3">
          <div
            v-for="(info, key) in headerInfoData"
            class="d-flex align-items-center text-muted small text-truncate info-item"
            :key="key"
            :data-test-id="key"
            :title="info.value"
            :class="{ clickable: key }"
            @click="onHeaderInfoClick()"
          >
            <div class="d-flex align-items-center">
              <font-awesome-icon :icon="info.icon" class="me-1" />
              <span class="text-truncate">{{ info.value }}</span>
            </div>
          </div>
        </div>
      </div>

      <div class="d-flex align-items-center flex-shrink-0 gap-1">
        <div class="d-flex">
          <SettingsBtn v-if="currentEpoch" />
          <EpochSizeSelect v-if="currentEpoch && isStudyDataLoaded" :epochSize="epochSize" />
          <NavEpochProgression v-if="currentEpoch" />
          <div class="vr mx-3"></div>
        </div>

        <button
          v-if="helpUrl"
          class="btn btn-secondary"
          :title="$t('userManual')"
          @click="openUserManual"
          data-test-id="btn-user-manual"
        >
          <font-awesome-icon icon="fa-solid fa-question" />
        </button>

        <button
          class="btn btn-secondary"
          :title="$t('deviceInformation')"
          @click="openDeviceModal"
          data-test-id="btn-device-info"
        >
          <font-awesome-icon icon="fa-solid fa-info" />
        </button>

        <a
          :href="isThirdPartyOrigin ? undefined : owlCoreRoute"
          class="btn btn-primary"
          :title="$t('navigateToStudyDetail')"
          @click="handleOwlCoreRedirection"
          data-test-id="btn-navigate-to-study-detail"
        >
          <font-awesome-icon icon="fa-solid fa-house-user" />
        </a>

        <router-link
          v-if="currentEpoch && studyId"
          :to="{ path: reportRoute, query: $route.query }"
          class="btn btn-primary"
          :title="$t('seeReport')"
          data-test-id="btn-see-report"
        >
          <font-awesome-icon icon="fa-solid fa-file-import" />
        </router-link>

        <router-link
          v-else-if="!showNavControl && studyId"
          :to="{ path: graphsRoute, query: $route.query }"
          class="btn btn-primary"
          :title="$t('seeGraphs')"
          data-test-id="btn-see-graphs"
        >
          <font-awesome-icon icon="fa-solid fa-chart-simple" />
        </router-link>
      </div>
    </div>
  </div>

  <!-- TODO: Create an individual component for device info! This logic should not be part of the header-bar -->
  <div v-if="isDeviceModalOpen" class="device-info-modal-overlay" @click.self="closeDeviceModal">
    <div class="device-info-modal-content">
      <h2>{{ $t('about') }}</h2>
      <div class="device-info-columns">
        <div class="device-info-left">
          <p>{{ deviceInfoConfig.company.name }}</p>
          <p>{{ deviceInfoConfig.company.addressLine1 }}</p>
          <p>{{ deviceInfoConfig.company.addressLine2 }}</p>
          <p>{{ deviceInfoConfig.company.cityCountry }}</p>
          <p>
            <span class="text-capitalize">{{ $t('phone') }}: </span>
            <span>{{ deviceInfoConfig.company.phone }}</span>
          </p>
          <p>
            <span class="text-capitalize">{{ $t('email') }}: </span>
            <span>{{ deviceInfoConfig.company.email }}</span>
          </p>
          <p>
            <span class="text-capitalize">{{ $t('website') }}: </span>
            <span>{{ deviceInfoConfig.company.website }}</span>
          </p>
          <br />
          <p>{{ $t(deviceInfoConfig.device.appliesToTranslKey) }}</p>
          <p>
            <span class="text-capitalize">{{ $t('releaseDate') }}: </span>
            <span>{{ deviceInfoConfig.device.releaseDate }}</span>
          </p>
          <p>
            <span class="text-uppercase">{{ $t('udi') }}: </span>
            <span>{{ deviceInfoConfig.device.udi }}</span>
          </p>
          <br />
          <p>{{ $t(deviceInfoConfig.descriptionTranslKey) }}</p>
        </div>

        <div class="device-info-right">
          <div class="row justify-content-center mt-2 mb-2">
            <div class="col-auto">
              <img src="@/assets/device-info/factory.png" alt="factory" />
            </div>
          </div>
          <!-- <div class="row justify-content-center mt-2 mb-2">
            <div class="col-auto">
              <img src="@/assets/device-info/ce1639.png" alt="ce1639" />
            </div>
          </div> -->
          <div class="row justify-content-center mt-2 mb-2">
            <div class="col-auto">
              <img src="@/assets/device-info/ref.png" alt="ref" />
            </div>
            <!-- <div class="col-auto">
              <img src="@/assets/device-info/md.png" alt="md" />
            </div> -->
            <div class="col-auto">
              <img src="@/assets/device-info/lot.png" alt="lot" />
            </div>
          </div>
          <div class="row justify-content-center mt-2 mb-2">
            <div class="col-auto">
              <img src="@/assets/device-info/iBook.png" alt="iBook" />
            </div>
            <div class="col-auto">
              <img src="@/assets/device-info/rxOnly.png" alt="rxOnly" />
            </div>
          </div>
        </div>
      </div>
      <button class="btn btn-secondary mt-3 text-capitalize" @click="closeDeviceModal">
        {{ $t('close') }}
      </button>
    </div>
  </div>
</template>

<script lang="ts">
import { ref, computed, onMounted } from 'vue';
import { useStore } from 'vuex';
import { useRoute } from 'vue-router';
import { EpochSizeSelect, NavEpochProgression, SettingsBtn } from '@/modules/epoch/common';
import {
  formatDateValue,
  capitalizeFirstLetter,
  axiosInstance,
  restoreCognitoIdentityToCookies,
  useOwlCoreStudyUrl
} from '@/common/utils';
import { DEVICE_INFO_CONFIG } from './deviceInfoConfig';
import { NotificationTypes } from '@/common/types';

export default {
  name: 'HeaderBar',
  components: {
    EpochSizeSelect,
    NavEpochProgression,
    SettingsBtn
  },
  emits: ['onPatientNameClick'],
  setup(_, { emit }) {
    const helpUrl = ref<string | null>(null);
    const store = useStore();
    const route = useRoute();

    const settings = computed(() => store.state.epoch.settings);
    const studyId = computed(() => route.params.id as string);
    const epochSize = computed(() => route.params.epochSize as string);
    const currentEpoch = computed(() => route.params.epochSelected || null);
    const routeName = computed(() => route.name as string);

    const isStudyDataLoaded = computed(() => store.getters['study/isStudyDataLoaded']);
    const scoreSetData = computed(() => store.getters['scoreSet/getScoreSet']);

    const headerInfoData = computed(() => {
      const { fullName = '', dateOfBirth = '', patientID = '' } = scoreSetData.value || {};
      return {
        patientName: { icon: 'fa-solid fa-user', value: fullName },
        patientDOB: {
          icon: 'fa-solid fa-calendar-day',
          value: dateOfBirth ? formatDateValue(new Date(dateOfBirth)) : ''
        },
        patientID: { icon: 'fa-solid fa-id-card', value: patientID },
        studyId: { icon: 'fa-solid fa-book-medical', value: studyId.value }
      };
    });

    const reportRoute = computed(() => `/report/${studyId.value}`);
    const graphsRoute = computed(() => {
      const { band, notch } = settings.value;

      return `/graphs/${studyId.value}/${capitalizeFirstLetter(notch)}/${capitalizeFirstLetter(band)}/30/0/`;
    });

    const showNavControl = computed(() =>
      ['StudyId', 'EpochView', 'EpochViewUnscored'].includes(routeName.value)
    );

    const owlCoreRoute = useOwlCoreStudyUrl();
    const isThirdPartyOrigin = computed(() => store.getters['user/isThirdPartyOrigin']);

    const handleOwlCoreRedirection = () => {
      store.dispatch('epoch/deleteClaim', studyId.value);
      store.commit('scoreSet/clearScoreSetData');

      if (isThirdPartyOrigin.value) {
        window.close();

        return;
      }

      restoreCognitoIdentityToCookies();
    };

    const onHeaderInfoClick = () => {
      emit('onPatientNameClick');
    };

    const fetchHelpUrl = async () => {
      try {
        const response = await axiosInstance.get(
          `${(import.meta as any).env.VITE_SIGNALS_API_URL}/help`
        );
        const data = response.data;

        if (data?.length > 0) helpUrl.value = data[0];
      } catch {
        store.commit(
          'addNotification',
          {
            i18nTitle: 'fetchError',
            i18nDescription: 'fetchErrorDescription',
            type: NotificationTypes.Error
          },
          { root: true }
        );
      }
    };

    const isDeviceModalOpen = ref(false);
    const openDeviceModal = () => (isDeviceModalOpen.value = true);
    const closeDeviceModal = () => (isDeviceModalOpen.value = false);

    onMounted(() => {
      fetchHelpUrl();
      if (!scoreSetData.value || Object.keys(scoreSetData.value).length === 0) {
        store.dispatch('scoreSet/getScoreSetByStudy', { sleepStudyID: studyId.value });
      }
    });

    return {
      helpUrl,
      studyId,
      epochSize,
      currentEpoch,
      headerInfoData,
      reportRoute,
      graphsRoute,
      showNavControl,
      isStudyDataLoaded,
      openUserManual: () => helpUrl.value && window.open(helpUrl.value, '_blank'),
      owlCoreRoute,
      isThirdPartyOrigin,
      handleOwlCoreRedirection,
      isDeviceModalOpen,
      openDeviceModal,
      closeDeviceModal,
      deviceInfoConfig: DEVICE_INFO_CONFIG,
      onHeaderInfoClick
    };
  }
};
</script>

<style lang="scss" src="./header-bar.component.scss" scoped />
